import React, { useEffect, useMemo, useState } from "react";
import { Clock, PhoneCall } from "lucide-react";
import { careersApi } from "../../../services/careersApi";
import { useLocalization } from "../../../hooks/useLocalization";

const formatDateLabel = (dateStr, language) => {
  const d = new Date(`${dateStr}T00:00:00+09:00`);
  return d.toLocaleDateString(language === "ja" ? "ja-JP" : "en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "Asia/Tokyo" });
};

export const InterviewStep = ({ form, update, job }) => {
  const { t, language } = useLocalization();
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [holding, setHolding] = useState(false);

  useEffect(() => {
    careersApi.getInterviewAvailability(job.id).then((r) => {
      setDays(r.data);
      const firstOpen = r.data.find((d) => d.open && d.slots.some((s) => s.available));
      if (firstOpen) setSelectedDate(firstOpen.date);
    }).finally(() => setLoading(false));
  }, [job.id]);

  const openDays = useMemo(() => days.filter((d) => d.open), [days]);
  const activeDay = days.find((d) => d.date === selectedDate);

  const pickSlot = async (startTime) => {
    if (form.interview_hold?.hold_token) {
      careersApi.releaseInterviewHold(form.interview_hold.hold_token).catch(() => {});
    }
    setHolding(true);
    try {
      const { data } = await careersApi.createInterviewHold({ job_posting_id: job.id, date: selectedDate, start_time: startTime });
      update({
        interview_hold: { date: selectedDate, start_time: startTime, hold_token: data.hold_token, hold_expires_at: data.hold_expires_at, protected_block_end: data.protected_block_end },
        interview_contact_me: false,
      });
    } catch {
      // Slot was taken between fetch and click - refresh availability so it disappears.
      careersApi.getInterviewAvailability(job.id).then((r) => setDays(r.data));
    } finally {
      setHolding(false);
    }
  };

  const toggleContactMe = () => {
    if (!form.interview_contact_me && form.interview_hold?.hold_token) {
      careersApi.releaseInterviewHold(form.interview_hold.hold_token).catch(() => {});
      update({ interview_hold: null });
    }
    update({ interview_contact_me: !form.interview_contact_me });
  };

  return (
    <div data-testid="wizard-step-interview" className="space-y-4">
      <p className="text-xs text-gray-500">{t("careers.wizard.interviewIntro")}</p>

      {loading ? (
        <p className="text-sm text-gray-500" data-testid="interview-availability-loading">{t("common.loading")}</p>
      ) : (
        <>
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="interview-date-picker">
            {openDays.map((d) => (
              <button
                key={d.date}
                type="button"
                data-testid={`interview-date-${d.date}`}
                onClick={() => setSelectedDate(d.date)}
                className={`shrink-0 px-3 py-2 rounded-xl text-xs font-semibold border transition-colors ${
                  selectedDate === d.date ? "bg-blue-950 text-white border-blue-950" : "bg-white text-gray-600 border-gray-300 hover:border-blue-950"
                }`}
              >
                {formatDateLabel(d.date, language)}
              </button>
            ))}
          </div>

          {activeDay && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="interview-time-picker">
              {activeDay.slots.map((s) => {
                const isSelected = form.interview_hold?.date === selectedDate && form.interview_hold?.start_time === s.start_time;
                return (
                  <button
                    key={s.start_time}
                    type="button"
                    disabled={!s.available || holding}
                    data-testid={`interview-slot-${selectedDate}-${s.start_time}`}
                    onClick={() => pickSlot(s.start_time)}
                    className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                      isSelected ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-gray-700 border-gray-300 hover:border-blue-950"
                    }`}
                  >
                    {s.start_time}
                  </button>
                );
              })}
            </div>
          )}

          {form.interview_hold && (
            <p data-testid="interview-hold-confirmation" className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <Clock size={14} className="shrink-0" />
              {t("careers.wizard.holdConfirmedPrefix")} {formatDateLabel(form.interview_hold.date, language)} @ {form.interview_hold.start_time} JST
            </p>
          )}
        </>
      )}

      <label className="flex items-center gap-2 text-sm text-gray-700 pt-2 border-t border-gray-100">
        <input data-testid="interview-contact-me-checkbox" type="checkbox" checked={form.interview_contact_me} onChange={toggleContactMe} className="shrink-0" />
        <PhoneCall size={14} className="text-blue-950" />
        <span>{t("careers.wizard.contactMeOption")}</span>
      </label>
    </div>
  );
};
